// Get the base URL dynamically
const getBaseUrl = () => {
  // If explicitly provided via environment variables, prioritize it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // In development, use localhost
  if (process.env.NODE_ENV === 'development') {
    // Force localhost:8000 for development to ensure consistent behavior
    console.log('🔧 Development environment detected - Using localhost:8000');
    return "http://localhost:8000";
  }
  
  // In production, derive from the current hostname
  const hostname = window.location.hostname;
  const port = window.location.port;
  const protocol = window.location.protocol;
  
  console.log(`🔧 Config Debug - Hostname: ${hostname}, Port: ${port}, Protocol: ${protocol}`);
  
  // For IP addresses, use the same IP with port 8000
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    const ipApiUrl = `${protocol}//${hostname}:8000`;
    console.log(`🌐 IP Environment detected - Using API URL: ${ipApiUrl}`);
    return ipApiUrl;
  }
  
  // For domains that include 'tudip', use the current hostname with backend port
  if (hostname.includes('tudip')) {
    const tudipApiUrl = `${protocol}//${hostname}:8000`;
    console.log(`🏢 Tudip Environment detected - Using API URL: ${tudipApiUrl}`);
    return tudipApiUrl;
  }
  
  // Default to API subdomain convention for production
  const defaultApiUrl = `${protocol}//api.${hostname}`;
  console.log(`🏭 Production Environment - Using API URL: ${defaultApiUrl}`);
  return defaultApiUrl;
};

// Use the dynamic API URL determination
export const API_URL = getBaseUrl();

// Log the final API URL for debugging
console.log(`🚀 Final API_URL configuration: ${API_URL}`);

// App version
export const APP_VERSION = process.env.REACT_APP_VERSION || "0.1.0";

// CORS and Development configuration
export const CORS_CONFIG = {
  // For development, we allow all origins and disable credentials
  mode: "cors" as RequestMode,
  credentials: "include" as RequestCredentials, // Changed to include to fix CORS issues
  cache: "no-cache" as RequestCache,
};

// Authentication configuration
export const AUTH_TOKEN_KEY = "authToken";
export const USER_DATA_KEY = "currentUser";

// Retry configuration
export const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // 1 second delay between retries
};

// API endpoints
export const ENDPOINTS = {
  LOGIN: "/api/v1/auth/login",
  CURRENT_USER: "/api/v1/users/me",
  USERS: "/api/v1/users/",
  PURCHASE_ORDERS: "/api/v1/purchase-orders",
  INVOICES: "/api/v1/invoices",
  INVOICES_WITH_QUOTES: "/api/v1/invoices-with-quotes",
  CUSTOMERS: "/api/v1/customers",
  PRICE_LISTS: "/api/v1/price-lists",
  SUPPLIERS: "/api/v1/suppliers/",
  ROLES: "/api/v1/roles",
  USER_ROLES: "/api/v1/roles",
  STORE_TYPES: "/api/v1/store-types/",
  MANAGERS: "/api/v1/users/managers",
  GLOBAL_SEARCH: "/api/v1/search",
  // Quote endpoints - ensure consistent naming
  QUOTES: "/api/v1/quotes",
  QUOTES_CURRENT: "/api/v1/quotes/current",
  QUOTES_CURRENT_COMPAT: "/api/v1/quotes/current-compat", // Keep compatibility endpoint as fallback
  QUOTES_ARCHIVED: "/api/v1/quotes/archived",
  QUOTES_REPORTS: "/api/v1/reports/quotes",
  // Reports endpoints
  REPORTS_PURCHASE_ORDERS: "/api/v1/reports/purchase-orders",
};

// Helper function to get full API URL for debugging
export const getFullApiUrl = (endpoint: string): string => {
  const fullUrl = `${API_URL}${endpoint}`;
  console.log(`🔗 Full API URL: ${fullUrl}`);
  return fullUrl;
};

// Environment detection helper
export const getEnvironmentInfo = () => {
  const info = {
    nodeEnv: process.env.NODE_ENV,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
    port: typeof window !== 'undefined' ? window.location.port : 'unknown',
    protocol: typeof window !== 'undefined' ? window.location.protocol : 'unknown',
    apiUrl: API_URL,
    isIPAddress: typeof window !== 'undefined' && /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname),
    isDevelopment: process.env.NODE_ENV === 'development'
  };
  
  console.log('🔍 Environment Info:', info);
  return info;
};

// Helper for API error logging
export const logApiError = (endpoint: string, error: any) => {
  console.error(`🔴 API Error on ${endpoint}:`, error);
  
  // Extract useful information from the error
  const errorInfo = {
    message: error?.message || 'Unknown error',
    status: error?.response?.status,
    statusText: error?.response?.statusText,
    data: error?.response?.data,
  };
  
  console.error('🔴 Error details:', errorInfo);
  return errorInfo;
};

const config = {
  API_URL,
  APP_VERSION,
  AUTH_TOKEN_KEY,
  USER_DATA_KEY,
  RETRY_CONFIG,
  ENDPOINTS,
  getFullApiUrl,
  getEnvironmentInfo,
  logApiError,
};

export default config;
